import type { Api } from "./client";

export interface LoginSuccess {
  access_token: string;
  refresh_token: string;
  expires_in: number;
  token_type: "Bearer";
  account: {
    id: string;
    email: string;
    display_name: string;
    role: string;
  };
  device: { id: string; label: string };
}

export interface Login2FAChallenge {
  requires_2fa: true;
  temp_token: string;
  expires_in: number;
}

export type LoginResponse = LoginSuccess | Login2FAChallenge;

export async function login(api: Api, email: string, password: string, deviceLabel?: string): Promise<LoginResponse> {
  return api.json<LoginResponse>("/auth/login", {
    method: "POST",
    body: JSON.stringify({ email, password, device_label: deviceLabel ?? "account-ui", platform: "web" }),
  });
}

export async function login2FA(api: Api, tempToken: string, code: string): Promise<LoginSuccess> {
  return api.json<LoginSuccess>("/auth/login/2fa", {
    method: "POST",
    body: JSON.stringify({ temp_token: tempToken, code }),
  });
}

export async function me(api: Api) {
  return api.json<{
    account: {
      id: string; email: string; display_name: string; role: string;
      acct_kind: string; two_factor_enabled: boolean;
    };
    device: { id: string };
    servers: Array<{
      id: string; server_id: string; relationship: string; label: string; last_seen_at: string | null;
    }>;
  }>("/auth/me");
}

export async function refresh(api: Api, refreshToken: string): Promise<LoginSuccess> {
  return api.json<LoginSuccess>("/auth/refresh", {
    method: "POST",
    body: JSON.stringify({ refresh_token: refreshToken }),
  });
}

export async function logout(api: Api, refreshToken: string) {
  return api.json<{ revoked: number }>("/auth/logout", {
    method: "POST",
    body: JSON.stringify({ refresh_token: refreshToken }),
  });
}

export async function logoutEverywhere(api: Api) {
  return api.json<{ revoked: number }>("/auth/logout-everywhere", { method: "POST" });
}

export interface EnrollResponse {
  provisioning_uri: string;
  secret_base32: string;
  recovery_codes: string[];
}

export async function twofaEnroll(api: Api): Promise<EnrollResponse> {
  return api.json<EnrollResponse>("/auth/2fa/enroll", { method: "POST" });
}

export async function twofaVerify(api: Api, code: string) {
  return api.json<{ enabled_at: string }>("/auth/2fa/verify", {
    method: "POST",
    body: JSON.stringify({ code }),
  });
}

export interface ServerRow {
  id: string; server_id: string; relationship: string; label: string;
  paired_at: string | null; last_seen_at: string | null;
  // Heartbeat-maintained; drive the entry-steering links (lib/serverLinks.ts).
  // Always present in the response — typed optional so older cached
  // fixtures that predate these columns still satisfy the shape.
  last_lan_ip?: string | null; last_lan_port?: number | null; last_lan_ip_at?: string | null;
}

export async function listServers(api: Api) {
  return api.json<{ servers: ServerRow[] }>("/account/servers");
}

export async function pairServer(api: Api, pairCode: string, label?: string) {
  return api.json<{
    user_server: { id: string; server_id: string; label: string; paired_at: string };
  }>("/account/servers/pair", {
    method: "POST",
    body: JSON.stringify({ pair_code: pairCode, label }),
  });
}

export async function revokeServer(api: Api, id: string) {
  return api.json<{ revoked: true }>(`/account/servers/${id}`, { method: "DELETE" });
}

export async function approveDeviceCode(api: Api, userCode: string, deviceLabel?: string) {
  return api.json<{ approved: true; device_label: string }>("/auth/device/approve", {
    method: "POST",
    body: JSON.stringify({ user_code: userCode, device_label: deviceLabel }),
  });
}

export interface DeviceRow {
  id: string;
  label: string;
  platform: string;
  user_agent: string | null;
  last_ip: string | null;
  last_seen_at: string;
  created_at: string;
  revoked_at: string | null;
  last_refresh_at: string | null;
}

export async function listDevices(api: Api) {
  return api.json<{ current_device_id: string; devices: DeviceRow[] }>("/account/devices");
}

export async function revokeDevice(api: Api, id: string) {
  return api.json<{ revoked: true }>(`/account/devices/${id}`, { method: "DELETE" });
}

export interface InviteRow {
  id: string;
  email: string | null;
  role: string;
  server_scope: string[] | null;
  issued_at: string;
  expires_at: string;
  status: "pending" | "redeemed" | "expired" | "revoked";
  redeemed_by_account_id: string | null;
}

export async function listInvites(api: Api) {
  return api.json<{ invites: InviteRow[] }>("/account/invites");
}

export async function createInvite(api: Api, body: { email?: string; role?: string; expires_in_days?: number }) {
  return api.json<{ invite: { id: string; code: string; role: string; email: string | null; expires_at: string } }>(
    "/account/invites",
    { method: "POST", body: JSON.stringify(body) },
  );
}

export async function revokeInvite(api: Api, id: string) {
  return api.json<{ revoked: true }>(`/account/invites/${id}`, { method: "DELETE" });
}

export async function changePassword(api: Api, currentPassword: string, newPassword: string) {
  return api.json<{
    changed: true;
    access_token: string;
    refresh_token: string;
    expires_in: number;
  }>("/auth/password/change", {
    method: "POST",
    body: JSON.stringify({ current_password: currentPassword, new_password: newPassword }),
  });
}

export async function regenerateRecoveryCodes(api: Api) {
  return api.json<{ recovery_codes: string[] }>("/auth/2fa/regenerate-recovery", { method: "POST" });
}

export async function disable2FA(api: Api, code: string) {
  return api.json<{ disabled: true }>("/auth/2fa/disable", {
    method: "POST",
    body: JSON.stringify({ code }),
  });
}

export interface AuthEventRow {
  id: number;
  ts: string;
  device_id: string | null;
  event_type: string;
  ip: string | null;
  user_agent: string | null;
  detail: Record<string, unknown> | null;
}

export async function listAuthEvents(api: Api, limit = 100, before?: string) {
  const qs = new URLSearchParams({ limit: String(limit) });
  if (before) qs.set("before", before);
  return api.json<{ events: AuthEventRow[] }>(`/account/auth-events?${qs.toString()}`);
}

// GET /auth/invite — read-only check account-ui's /join page runs before
// showing the registration form. See workers/auth/src/routes/invites.ts:
// never returns the invite's pinned email, only whether one exists.
export interface InviteCheckResponse {
  status: "valid" | "invalid" | "expired" | "redeemed" | "revoked";
  role?: string;
  expires_at?: string;
  email_pinned?: boolean;
}

export async function checkInvite(api: Api, code: string): Promise<InviteCheckResponse> {
  return api.json<InviteCheckResponse>(`/auth/invite?code=${encodeURIComponent(code)}`);
}

export interface RegisterWithInviteResponse {
  access_token: string;
  refresh_token: string;
  expires_in: number;
  token_type: "Bearer";
  account: { id: string; email: string; display_name: string; role: string; acct_kind: string };
  device: { id: string; label: string };
  next_step: string;
}

export async function registerWithInvite(
  api: Api,
  body: { code: string; email: string; display_name: string; password: string },
): Promise<RegisterWithInviteResponse> {
  return api.json<RegisterWithInviteResponse>("/auth/register-with-invite", {
    method: "POST",
    body: JSON.stringify({ ...body, device_label: "account-ui", platform: "web" }),
  });
}

// Self-service hard delete — workers/auth/src/routes/account.ts's
// `DELETE /account`. 200 on success (`deleted: true` + a best-effort
// webhook-delivery summary the UI doesn't need); 409 `has_dependent_accounts`
// when the account still owns live invited members (see
// lib/deleteAccountFlow.ts for how the UI maps that).
export async function deleteAccount(api: Api) {
  return api.json<{ deleted: true; webhook: unknown }>("/account", { method: "DELETE" });
}
